import React, { useState, useMemo, useRef, useEffect } from "react";
import { Switch, Typography, Box, Button } from "@material-ui/core";
import { useTranslation } from "@/langs/useTranslation";
import DataTable from "@/components/DataTable";
import { ReactRender } from "@/components/ReactRender";
import dayjs from "dayjs";
import AddAppModal from "./AddAppModal";
import Version from "./Version";
import UploadAndroidAppDialog from "@/containers/AndroidApp/UploadAndroidAppDialog";
import { useLocation } from "react-router-dom";

const AppConfig = ({ values, onChange, readOnly }) => {
  const { t } = useTranslation();
  const [addAppOpen, setAddAppOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const { pathname } = useLocation()
  const tableRef = useRef()

  useEffect(() => {
    tableRef.current?.reload()
  }, [pathname])

  const valuesRef = useRef();
  valuesRef.current = values;
  const columns = [
    {
      data: "name",
      title: t({ defaultMessage: "程序名称" }),
      orderable: false
    },
    {
      data: "version",
      title: t({ defaultMessage: "版本" }),
      orderable: false,
      createdCell: (td, data, row) => {
        const appIndex = valuesRef.current.applications.findIndex(
          app => app.package_id === row.package_id
        );
        ReactRender(
          <Version
            data={row}
            value={valuesRef.current.applications[appIndex].version}
            onChange={onChange}
            // name={`application.applications[${appIndex}].version`}
            appIndex={appIndex}
          />,
          td
        );
      }
    },
    {
      data: "version_code",
      title: t({ defaultMessage: "版本代码" }),
      orderable: false
    },
    {
      data: "upload_at",
      title: t({ defaultMessage: "上传时间" }),
      orderable: false,
      render: data => dayjs(data).format("YYYY-MM-DD HH:mm:ss")
    },
    {
      data: "size",
      title: t({ defaultMessage: "大小" }),
      className: "center",
      orderable: false,
      render: data => `${(data / (1024 * 1024)).toFixed(2)} MB`
    },
    {
      data: "deleted_old",
      title: t({ defaultMessage: "是否删除原程序" }),
      className: "center",
      orderable: false,
      createdCell: (td, data, row) => {
        const appIndex = valuesRef.current.applications.findIndex(
          app => app.package_id === row.package_id
        );
        ReactRender(
          <div
            onClick={() => {
              onChange({
                target: {
                  name: `application.applications[${appIndex}].deleted_old`,
                  value: !valuesRef.current.applications[appIndex].deleted_old
                }
              });
            }}
          >
            <Switch
              readOnly
              // checked={data}
              color="primary"
              size="small"
              // name={`application.applications[${appIndex}].delete_old`}
              checked={valuesRef.current.applications[appIndex].deleted_old}
            />
          </div>,
          td
        );
      }
    },
    {
      data: null,
      title: t({ defaultMessage: "操作" }),
      className: "center nowrap",
      searchable: false,
      orderable: false,
      width: 80,
      createdCell: (td, data, row) => {
        const appIndex = valuesRef.current.applications.findIndex(
          app => app.package_id === row.package_id
        );
        ReactRender(
          <Button
            size="small"
            color="primary"
            onClick={() => {
              const tmp = [...valuesRef.current.applications];
              tmp.splice(appIndex, 1);
              onChange({
                target: {
                  name: "application.applications",
                  value: tmp
                }
              });
            }}
          >
            {t({ defaultMessage: "移除" })}
          </Button>,
          td
        );
      }
    }
  ];
  const checked = useMemo(() => {
    const tmp = {};
    values.applications?.forEach(app => {
      tmp[`${app.package_id}|||${app.version}|||${app.id}`] = app;
    });
    return tmp;
  }, [values.applications]);

  return (
    <Box flex={1}>
      <Typography variant="h5" style={{ marginBottom: 32 }}>
        {t({ defaultMessage: "应用管理" })}
      </Typography>
      <Box display="flex" alignItems="center" flex={1}>
        <Typography variant="h6" style={{ marginRight: "auto" }}>
          {t({ defaultMessage: "是否启用" })}
        </Typography>
        <Switch
          name="application.open"
          checked={values.open}
          color="primary"
          onChange={onChange}
          size="small"
        />
      </Box>
      {values.open && (
        <>
          <Button
            variant="outlined"
            color="primary"
            style={{ minWidth: 118, marginTop: 24, marginBottom: 24 }}
            onClick={() => setAddAppOpen(true)}
            disabled={readOnly}
          >
            {t({ defaultMessage: "添加应用" })}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            style={{ minWidth: 118, marginTop: 24, marginBottom: 24, marginLeft: 16 }}
            onClick={() => setUploadOpen(true)}
            disabled={readOnly}
          >
            {t({ defaultMessage: "上传本地应用" })}
          </Button>
          <DataTable
            ref={tableRef}
            options={{ serverSide: false, paging: false }}
            data={values.applications}
            ajax={null}
            columns={columns}
            paging={false}
            showCheckAll={false}
            showFooter={false}
            // autoLoad
          />
          <UploadAndroidAppDialog
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            onSuccess={data => {
              onChange({
                target: {
                  name: "application.applications",
                  value: [...values.applications, {
                    deleted_old: false,
                    file_path: data.file_path,
                    id: data.id,
                    md5: data.md5,
                    name: data.name,
                    package_id: data.package_id,
                    size: data.size,
                    upload_at: data.upload_at,
                    version: data.version,
                    version_code: data.version_code
                  }]
                }
              })
            }}
          />
          {addAppOpen && (
            <AddAppModal
              open={addAppOpen}
              onClose={() => setAddAppOpen(false)}
              onConfirm={data => {
                onChange({
                  target: {
                    name: "application.applications",
                    value: data
                  }
                });
                setAddAppOpen(false);
              }}
              checked={checked}
            />
          )}
        </>
      )}
    </Box>
  );
};

export const initValues = { open: false, applications: [] };
export default AppConfig;
